/**
 * The login wall + paywall stub shown by the entry gate (sc-6940).
 *
 * Login wall: sells the 5 free daily tokens (not just "sign in"), offers
 * [Continue with Google] (primary, low-friction) and an email fallback
 * (magic-link today; email-OTP fast-follow when Zyphr ships it, M2v-2a).
 *
 * Both auth paths leave the page (Google redirect / magic-link email),
 * so we stash the cartridge the player was trying to coin and relaunch
 * it after the /auth/callback round-trip (consumePendingLaunch()).
 *
 * Paywall (grant:'paywall') is a STUB until sc-6944 builds the real
 * pricing surface — it states the situation and the reset time; passes
 * aren't purchasable yet.
 */

import { oauthStart } from '../api/client.js';
import { sendMagicLink } from '../auth/api-client.js';

const PENDING_LAUNCH_KEY = 'a84.pendingLaunch';
const OAUTH_STATE_KEY = 'a84.oauth.state';
const OAUTH_PROVIDER_KEY = 'a84.oauth.provider';

/** Remember the cartridge to auto-launch after the auth round-trip. */
export function setPendingLaunch(cartridgeId: string): void {
  try {
    sessionStorage.setItem(PENDING_LAUNCH_KEY, cartridgeId);
  } catch {
    /* private mode — auto-relaunch just won't happen; login still works */
  }
}

/** Read + clear the pending cartridge (called on boot after callback). */
export function consumePendingLaunch(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_LAUNCH_KEY);
    sessionStorage.removeItem(PENDING_LAUNCH_KEY);
    return id;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- overlay

let activeWall: HTMLElement | null = null;

function dismiss(): void {
  activeWall?.remove();
  activeWall = null;
}

/**
 * Show the login wall for a cartridge. Returns a promise that resolves
 * when the player dismisses it (back to gallery); the auth actions
 * navigate away, so those paths never resolve here.
 */
export function showLoginWall(cartridgeId: string, cartridgeTitle: string): Promise<void> {
  return new Promise((resolve) => {
    dismiss();
    const { root, googleBtn, emailBtn, emailForm, emailInput, emailSend, status, closeBtn } =
      buildWall('login', cartridgeTitle);
    activeWall = root;
    document.body.appendChild(root);

    const close = (): void => { dismiss(); resolve(); };
    closeBtn.addEventListener('click', close);
    root.addEventListener('click', (e) => { if (e.target === root) close(); });

    // Google: stash the pending cartridge + provider, ask the API for the
    // authorization URL, then redirect. `state` is stashed for the
    // callback's CSRF check.
    googleBtn.addEventListener('click', () => {
      void (async () => {
        googleBtn.disabled = true;
        status.textContent = 'Redirecting to Google…';
        try {
          setPendingLaunch(cartridgeId);
          const { authorizationUrl, state } = await oauthStart(
            'google',
            `${window.location.origin}/auth/callback`
          );
          sessionStorage.setItem(OAUTH_STATE_KEY, state);
          sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'google');
          window.location.assign(authorizationUrl);
        } catch (err) {
          googleBtn.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't start Google sign-in: ${err.message}` : 'Sign-in failed.';
        }
      })();
    });

    // Email fallback: reveal the input, send a magic link.
    emailBtn.addEventListener('click', () => {
      emailForm.hidden = false;
      emailInput.focus();
    });
    const sendEmail = (): void => {
      const email = emailInput.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        status.textContent = 'Enter a valid email address.';
        emailInput.focus();
        return;
      }
      status.textContent = 'Sending your sign-in link…';
      emailSend.disabled = true;
      setPendingLaunch(cartridgeId);
      void sendMagicLink({ email, redirectUrl: `${window.location.origin}/auth/callback` })
        .then(() => { status.textContent = 'Check your email for a sign-in link.'; })
        .catch((err) => {
          emailSend.disabled = false;
          status.textContent =
            err instanceof Error ? `Couldn't send the link: ${err.message}` : 'Send failed.';
        });
    };
    emailSend.addEventListener('click', sendEmail);
    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); sendEmail(); }
    });
  });
}

/** Paywall stub (grant:'paywall') — real pricing lands in sc-6944. */
export function showPaywallStub(resetsAtUtc?: string): Promise<void> {
  return new Promise((resolve) => {
    dismiss();
    const { root, closeBtn, status } = buildWall('paywall', '');
    activeWall = root;
    if (resetsAtUtc) {
      const reset = new Date(resetsAtUtc);
      const mins = Math.max(0, Math.round((reset.getTime() - Date.now()) / 60000));
      const h = Math.floor(mins / 60);
      status.textContent = h > 0
        ? `Free tokens refill in about ${h}h ${mins % 60}m.`
        : `Free tokens refill in about ${mins}m.`;
    }
    document.body.appendChild(root);
    const close = (): void => { dismiss(); resolve(); };
    closeBtn.addEventListener('click', close);
    root.addEventListener('click', (e) => { if (e.target === root) close(); });
  });
}

// ---------------------------------------------------------------- DOM

interface WallRefs {
  root: HTMLElement;
  googleBtn: HTMLButtonElement;
  emailBtn: HTMLButtonElement;
  emailForm: HTMLElement;
  emailInput: HTMLInputElement;
  emailSend: HTMLButtonElement;
  status: HTMLElement;
  closeBtn: HTMLButtonElement;
}

function buildWall(kind: 'login' | 'paywall', title: string): WallRefs {
  const root = document.createElement('div');
  root.className = 'play-gate-wall';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');

  const card = document.createElement('div');
  card.className = 'play-gate-wall__card';

  const eyebrow = document.createElement('div');
  eyebrow.className = 'play-gate-wall__eyebrow';
  eyebrow.textContent = kind === 'login' ? 'INSERT COIN' : 'OUT OF TOKENS';

  const heading = document.createElement('h2');
  heading.className = 'play-gate-wall__title';
  heading.textContent = kind === 'login'
    ? 'Sign in to keep playing'
    : "You're out of tokens";

  const pitch = document.createElement('p');
  pitch.className = 'play-gate-wall__pitch';
  pitch.textContent = kind === 'login'
    ? 'Free account = 5 plays every day. No card, no catch.'
    : 'Come back tomorrow for 5 more — or grab a pass (coming soon).';

  card.append(eyebrow, heading, pitch);

  // login-only auth controls
  const googleBtn = document.createElement('button');
  const emailBtn = document.createElement('button');
  const emailForm = document.createElement('div');
  const emailInput = document.createElement('input');
  const emailSend = document.createElement('button');
  if (kind === 'login') {
    googleBtn.type = 'button';
    googleBtn.className = 'play-gate-wall__google';
    googleBtn.textContent = 'Continue with Google';

    emailBtn.type = 'button';
    emailBtn.className = 'play-gate-wall__email-toggle';
    emailBtn.textContent = 'Use email instead';

    emailForm.className = 'play-gate-wall__email-form';
    emailForm.hidden = true;
    emailInput.type = 'email';
    emailInput.placeholder = 'you@example.com';
    emailInput.className = 'play-gate-wall__email-input';
    emailInput.autocomplete = 'email';
    emailSend.type = 'button';
    emailSend.className = 'play-gate-wall__email-send';
    emailSend.textContent = 'Email me a link';
    emailForm.append(emailInput, emailSend);

    card.append(googleBtn, emailBtn, emailForm);
  }

  const status = document.createElement('p');
  status.className = 'play-gate-wall__status';
  status.setAttribute('aria-live', 'polite');
  card.append(status);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'play-gate-wall__close';
  closeBtn.textContent = kind === 'login' ? 'Maybe later' : 'Back to cabinet';
  card.append(closeBtn);

  root.append(card);
  void title; // reserved for a future "…to play NEON GRIND" personalization
  return { root, googleBtn, emailBtn, emailForm, emailInput, emailSend, status, closeBtn };
}
